// ─── Moteur d'occurrences : prévu vs réalisé (module PUR) ─────────
// Cf. CONCEPTION-FINANCE.md. Une LIGNE PRÉVUE (« Loyer −850 €/mois depuis
// 01/2025 ») engendre une OCCURRENCE par mois. Les occurrences sont CALCULÉES
// (aucun document) ; seules celles qu'on annote sont matérialisées dans
// `occurrencesBudget` — d'où l'argument `exceptions` ici.
//
// Une occurrence est une ENVELOPPE du mois : plusieurs actuals peuvent s'y
// rattacher (le loyer en reçoit 1, « Courses » en reçoit 8), et l'écart se
// calcule sur la SOMME. C'est ce que MoPCal ne savait pas faire (statut binaire).

// Extension `.js` OBLIGATOIRE : Metro résout sans, Node non — et ce module est
// couvert par `npm test` (piège consigné dans COWORK.md).
import { estImprevue } from './repartition.js';
import { estDueCredit, montantPrevuCredit, raisonNonDueCredit } from './credit.js';

const cent = (n) => Math.round((Number(n) || 0) * 100) / 100;
const RE_MOIS = /^\d{4}-\d{2}$/;

export const moisDeDate = (d) => String(d || '').slice(0, 7);
export const moisCourant = (auj) => String(auj || new Date().toISOString().slice(0, 10)).slice(0, 7);

// Arithmétique de mois ('AAAA-MM' + n).
export function decalerMois(mk, n) {
  const [y, m] = String(mk).split('-').map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}
export function moisEntre(debut, fin) {
  const out = [];
  if (!RE_MOIS.test(debut) || !RE_MOIS.test(fin) || fin < debut) return out;
  let mk = debut, garde = 0;
  while (mk <= fin && garde++ < 1200) { out.push(mk); mk = decalerMois(mk, 1); }
  return out;
}

const PAS = { mensuelle: 1, trimestrielle: 3, semestrielle: 6, annuelle: 12 };

// La ligne est-elle due ce mois-ci ?
// Nombre de mois entre deux clés 'AAAA-MM', bornes INCLUSES.
const nbMoisEntre = (a, b) => {
  const [ay, am] = String(a).split('-').map(Number);
  const [by, bm] = String(b).split('-').map(Number);
  return (by - ay) * 12 + (bm - am) + 1;
};

// PONCTUELLE ÉTALÉE — re-prévision DYNAMIQUE (Cédric, 16/08) : chaque mois
// restant porte (montant total − réel déjà constaté sur les mois PRÉCÉDENTS)
// ÷ nombre de mois restants, jamais négatif. 10 000 sur 4 mois → 2 500/mois ;
// 7 000 réels le mois 1 → les 3 suivants prévoient 1 000 chacun.
// Le mois affiché garde son propre réel en face : un dépassement se lit.
export function prevuEtaleDynamique(ligne, mk, rattachements = []) {
  const debut = moisDeDate(ligne?.debut);
  const fin = moisDeDate(ligne?.fin);
  const total = Math.abs(Number(ligne?.montant) || 0);
  let reelAvant = 0;
  for (const r of rattachements) {
    if (r?.ligneId !== ligne?.id) continue;
    if (!r.mois || r.mois < debut || r.mois >= mk) continue;
    reelAvant += Math.abs(r.montant || 0);
  }
  return cent(Math.max(0, (total - reelAvant) / Math.max(1, nbMoisEntre(mk, fin))));
}

export function estDue(ligne, mk) {
  if (!ligne || ligne.actif === false || !RE_MOIS.test(mk)) return false;
  // Une ligne à CRÉDIT (17/08) a sa propre courbe — l'échéancier d'amortissement
  // FAIT FOI, avant toute logique de fréquence/inflation ci-dessous.
  if (ligne.credit?.actif) return estDueCredit(ligne, mk);
  const debut = moisDeDate(ligne.debut);
  if (!RE_MOIS.test(debut) || mk < debut) return false;
  if (ligne.fin && mk > moisDeDate(ligne.fin)) return false;
  // PONCTUELLE ÉTALÉE (Cédric, 16/08) : avec une fin, le montant se répartit
  // sur la période — la ligne est due chaque mois entre début et fin (bornes
  // déjà vérifiées ci-dessus). Sans fin, un seul mois, comme avant.
  if (ligne.frequence === 'ponctuelle') return ligne.fin ? true : mk === debut;
  if (ligne.frequence === 'personnalisee') {
    return (ligne.echeancier || []).some(e => moisDeDate(e.date) === mk);
  }
  const pas = PAS[ligne.frequence] || 1;
  const [dy, dm] = debut.split('-').map(Number);
  const [y, m] = mk.split('-').map(Number);
  return (((y - dy) * 12 + (m - dm)) % pas) === 0;
}

// POURQUOI une ligne n'est-elle pas due ce mois-ci ? (11/08, demande de Cédric)
// `estDue` répond oui/non ; le non était jusqu'ici SILENCIEUX — la ligne
// disparaissait de l'écran sans qu'on puisse distinguer « pas due ce mois-ci »
// de « mal configurée » ou de « je l'ai rêvée ». C'est le miroir exact de
// `estDue` : toute branche qui y renvoie `false` a ici son motif.
//
// Renvoie `null` quand la ligne EST due. Sinon { motif, mois? } — le mois sert
// à l'affichage (« commence en 09/2026 »).
export function raisonNonDue(ligne, mk) {
  if (!ligne) return { motif: 'inexistante' };
  if (!RE_MOIS.test(mk)) return { motif: 'moisInvalide' };
  if (ligne.actif === false) return { motif: 'inactive' };
  if (ligne.credit?.actif) return raisonNonDueCredit(ligne, mk);

  const debut = moisDeDate(ligne.debut);
  if (!RE_MOIS.test(debut)) return { motif: 'sansDebut' };
  if (mk < debut) return { motif: 'avantDebut', mois: debut };
  const fin = ligne.fin ? moisDeDate(ligne.fin) : null;
  if (fin && mk > fin) return { motif: 'apresFin', mois: fin };

  if (ligne.frequence === 'ponctuelle') {
    // Étalée : les bornes début/fin ci-dessus ont déjà tranché.
    return (ligne.fin || mk === debut) ? null : { motif: 'ponctuelle', mois: debut };
  }
  if (ligne.frequence === 'personnalisee') {
    const dates = (ligne.echeancier || []).map(e => moisDeDate(e.date)).filter(Boolean).sort();
    if (dates.includes(mk)) return null;
    return { motif: 'horsEcheancier', mois: dates.find(d => d > mk) || dates[dates.length - 1] || null };
  }
  const pas = PAS[ligne.frequence] || 1;
  const [dy, dm] = debut.split('-').map(Number);
  const [y, m] = mk.split('-').map(Number);
  const ecart = (y - dy) * 12 + (m - dm);
  if (ecart % pas === 0) return null;
  // Prochain mois dû : on le donne, c'est ce qui rend le message utile.
  return { motif: 'frequence', mois: decalerMois(mk, pas - (((ecart % pas) + pas) % pas)) };
}

// Les lignes NON dues d'un mois, chacune avec son motif. Pendant de
// `occurrencesDuMois`, pour que l'écran puisse montrer ce qu'il écarte.
export function nonDuesDuMois({ lignes = [], exceptions = [], mk }) {
  const excParLigne = new Map();
  for (const e of exceptions) if (e?.ligneId && e.mois === mk) excParLigne.set(e.ligneId, e);
  return lignes
    .filter(l => l && !estDue(l, mk))
    .map(l => ({ ligne: l, ligneId: l.id, ...raisonNonDue(l, mk), exception: excParLigne.get(l.id) || null }));
}

// Montant PRÉVU d'une ligne pour un mois : échéancier si personnalisée, sinon
// montant de la ligne, avec inflation annuelle COMPOSÉE si elle est réglée.
export function montantPrevu(ligne, mk) {
  if (!ligne) return 0;
  // Crédit : la mensualité (+ assurance) DE L'ÉCHÉANCIER, jamais composée
  // avec `inflationTaux` — l'amortissement est déjà sa propre courbe.
  if (ligne.credit?.actif) return montantPrevuCredit(ligne, mk);
  if (ligne.frequence === 'personnalisee') {
    const e = (ligne.echeancier || []).find(x => moisDeDate(x.date) === mk);
    return cent(e ? e.montant : 0);
  }
  // PONCTUELLE ÉTALÉE : le prévu STATIQUE est le montant divisé par le nombre
  // de mois de la période. La re-prévision dynamique — qui tient compte du
  // réel déjà constaté — vit dans `occurrencesDuMois`, seul endroit qui voit
  // les rattachements.
  if (ligne.frequence === 'ponctuelle' && ligne.fin) {
    return cent((Number(ligne.montant) || 0) / Math.max(1, nbMoisEntre(moisDeDate(ligne.debut), moisDeDate(ligne.fin))));
  }
  const base = Number(ligne.montant) || 0;
  const taux = Number(ligne.inflationTaux) || 0;
  const depuis = moisDeDate(ligne.inflationDepuis || ligne.debut);
  if (!taux || !RE_MOIS.test(depuis) || mk <= depuis) return cent(base);
  const [ay, am] = depuis.split('-').map(Number);
  const [by, bm] = mk.split('-').map(Number);
  const annees = Math.floor(((by - ay) * 12 + (bm - am)) / 12);
  return cent(base * Math.pow(1 + taux / 100, Math.max(0, annees)));
}

// Montant signé (revenu positif, dépense négative) — pour les totaux.
export const signe = (ligne, montant) => (ligne?.sens === 'revenu' ? 1 : -1) * Math.abs(montant || 0);

// Mois d'imputation d'un actual : explicite, sinon déduit de la date effective.
export const moisImputation = (op) =>
  op?.moisImputation || moisDeDate(op?.dateBanque || op?.datePaiement || op?.date);

// ── Occurrences d'un mois ────────────────────────────────────────
// Renvoie une occurrence par ligne due, enrichie du réel et de son statut.
//   lignes     : lignesBudget
//   actuals    : depenses (déjà filtrées sur l'espace)
//   exceptions : occurrencesBudget (annotations)
//   mk         : 'AAAA-MM' ; auj : 'AAAA-MM-JJ' (injecté pour rester testable)
// `rattachements` (10/08) : quand la liste est fournie, elle FAIT FOI et le
// réel se calcule sur les PARTS affectées — c'est le modèle n↔n, où une
// opération peut n'honorer une ligne que pour une fraction de son montant.
// À `null`, on retombe sur l'ancien champ `depenses.ligneId` (un lien unique,
// montant entier). Ce repli est TRANSITOIRE : il disparaîtra avec le dernier
// écran migré. Deux sources de vérité en même temps seraient un piège — d'où
// le choix explicite par l'appelant, jamais une fusion des deux.
export function occurrencesDuMois({ lignes = [], actuals = [], exceptions = [], rattachements = null, mk, auj }) {
  const courant = moisCourant(auj);
  const futur = mk > courant;

  const reelParLigne = new Map();
  const rattachesParLigne = new Map();
  if (rattachements) {
    for (const r of rattachements) {
      if (!r?.ligneId || r.mois !== mk) continue;
      reelParLigne.set(r.ligneId, cent((reelParLigne.get(r.ligneId) || 0) + Math.abs(r.montant || 0)));
      rattachesParLigne.set(r.ligneId, (rattachesParLigne.get(r.ligneId) || 0) + 1);
    }
  } else {
    for (const op of actuals) {
      if (!op?.ligneId || op.exclu) continue;
      if (moisImputation(op) !== mk) continue;
      reelParLigne.set(op.ligneId, cent((reelParLigne.get(op.ligneId) || 0) + Math.abs(op.montant || 0)));
      rattachesParLigne.set(op.ligneId, (rattachesParLigne.get(op.ligneId) || 0) + 1);
    }
  }
  const excParLigne = new Map();
  for (const e of exceptions) if (e?.ligneId && e.mois === mk) excParLigne.set(e.ligneId, e);

  return lignes.filter(l => estDue(l, mk)).map(l => {
    const exc = excParLigne.get(l.id) || null;
    // Ponctuelle ÉTALÉE : avec les rattachements en main, le prévu du mois se
    // RE-CALCULE sur ce qui reste — sans eux (vues de plan), il reste statique.
    const prevuBase = (rattachements && l.frequence === 'ponctuelle' && l.fin)
      ? prevuEtaleDynamique(l, mk, rattachements)
      : montantPrevu(l, mk);
    const prevu = cent(exc && exc.montantAjuste != null ? exc.montantAjuste : prevuBase);
    const reel = cent(reelParLigne.get(l.id) || 0);
    const nb = rattachesParLigne.get(l.id) || 0;

    let statut;
    if (exc?.statut === 'ignoree') statut = 'ignoree';
    else if (exc?.statut === 'absente') statut = 'absente';
    // SOLDÉE (12/08, Cédric) : « c'est fini, même si c'est incomplet ». Sans
    // cet état, une occurrence sous-consommée restait éternellement
    // « partielle » et réclamait un complément qui ne viendrait jamais.
    // Distincte des deux autres : `absente` dit « ça n'a pas eu lieu »,
    // `ignoree` dit « ne compte pas », `soldee` dit « le reste ne viendra pas ».
    else if (exc?.statut === 'soldee') statut = 'soldee';
    else if (futur) statut = 'avenir';
    else if (nb === 0) statut = 'manquante';
    else if (reel + 0.005 < prevu) statut = 'partielle';
    else statut = 'realisee';

    return {
      ligne: l, ligneId: l.id, mois: mk, prevu, reel, nb, statut,
      ecart: cent(reel - prevu),                       // < 0 = reste à dépenser
      prevuSigne: signe(l, prevu), reelSigne: signe(l, reel),
      exception: exc,
    };
  });
}

// Actuals d'un mois qui ne sont rattachés à AUCUNE ligne prévue : les IMPRÉVUS.
// (Sur un mois futur la notion n'a pas de sens : il n'y a pas encore de réel.)
// Avec les rattachements, « imprévu » cesse d'être un booléen : une opération
// peut être rattachée ET garder une part qui n'appartient à aucune prévision.
// C'est cette part qui est imprévue, pas l'opération entière. On liste donc
// tout ce à quoi il RESTE quelque chose à affecter — la vue affiche le reste
// via `repartitionDepense`.
export function imprevusDuMois({ actuals = [], rattachements = null, mk }) {
  const duMois = actuals.filter(op => op && !op.exclu && moisImputation(op) === mk);
  if (!rattachements) return duMois.filter(op => !op.ligneId);
  return duMois.filter(op => estImprevue(op, rattachements));
}

// ── Plan d'une ENVELOPPE (onglet Budget) ─────────────────────────
// Le budget ESTIME, le compte CONSTATE. Ces deux helpers servent l'écran
// Budget, qui ne montre que du prévu — le réel se lit dans Comptes, et l'écart
// dans Suivi.

// Les lignes prévues d'une enveloppe. `null` cible celles qui n'en ont pas :
// sans cette entrée, une ligne sans enveloppe serait introuvable.
export const lignesDeBudget = (lignes = [], budgetId) =>
  lignes.filter(l => l && (l.budgetId || null) === (budgetId || null));

// Total prévu d'un mois, par enveloppe : { budgetId → total SIGNÉ }.
// Signé, car une enveloppe peut porter des revenus comme des dépenses (Cédric,
// 10/08 : « on prévoit les opérations, revenus et dépenses »). Un total en
// valeur absolue ferait apparaître un salaire comme une charge.
export function prevuDuMoisParBudget({ lignes = [], exceptions = [], mk, auj }) {
  const parBudget = new Map();
  const occ = occurrencesDuMois({ lignes, actuals: [], exceptions, mk, auj });
  for (const o of occ) {
    // Une occurrence explicitement écartée ne compte pas dans le plan : elle a
    // été annotée « n'aura pas lieu » ou « à ignorer ».
    if (o.statut === 'ignoree' || o.statut === 'absente') continue;
    const cle = o.ligne?.budgetId || null;
    parBudget.set(cle, cent((parBudget.get(cle) || 0) + o.prevuSigne));
  }
  return parBudget;
}

// Opérations mises HORS CALCUL (`exclu`) : virements entre ses propres comptes,
// régularisations… Elles ne pèsent dans aucun total, mais restent LISTÉES : une
// opération qui disparaît sans trace est une opération qu'on ne peut plus
// corriger.
export function internesDuMois({ actuals = [], mk }) {
  return actuals.filter(op => op && op.exclu && moisImputation(op) === mk);
}

// Totaux d'un jeu d'occurrences + imprévus (signés : revenus positifs).
export function totauxMois(occurrences = [], imprevus = []) {
  let prevu = 0, reel = 0, reste = 0, resteDebit = 0, resteCredit = 0;
  // Décomposition par SENS (Cédric, 15/08) : la synthèse affiche une ligne
  // débits, une ligne crédits, et le total. Montants ABSOLUS ici — l'écran
  // pose les signes selon la convention (débit −, crédit +).
  let prevuDebit = 0, prevuCredit = 0, reelDebit = 0, reelCredit = 0;
  for (const o of occurrences) {
    if (o.statut === 'ignoree') continue;              // volontairement hors calcul
    prevu += o.prevuSigne;
    reel += o.reelSigne;
    if (o.ligne?.sens === 'revenu') { prevuCredit += Math.abs(o.prevu || 0); reelCredit += Math.abs(o.reel || 0); }
    else { prevuDebit += Math.abs(o.prevu || 0); reelDebit += Math.abs(o.reel || 0); }
    const r = resteADepenser(o);
    reste += r;
    // Scinde par SENS (Cedric, 15/08) : « reste a depenser » melangait ce
    // qu'on va encore SORTIR et ce qu'on attend encore de RECEVOIR — deux
    // informations opposees dans un meme chiffre.
    if (o.ligne?.sens === 'revenu') resteCredit += r; else resteDebit += r;
  }
  for (const op of imprevus) {
    const m = Math.abs(op.montant || 0);
    reel += (op.sens === 'revenu' ? 1 : -1) * m;
    if (op.sens === 'revenu') reelCredit += m; else reelDebit += m;
  }
  // `reste` répond à « combien vais-je encore sortir ce mois-ci ? », qui n'est
  // PAS la même question que l'écart (Cédric, 12/08). L'écart compare un prévu
  // à un réel, y compris quand plus rien n'est attendu.
  return {
    prevu: cent(prevu), reel: cent(reel), ecart: cent(reel - prevu),
    reste: cent(reste), resteDebit: cent(resteDebit), resteCredit: cent(resteCredit),
    prevuDebit: cent(prevuDebit), prevuCredit: cent(prevuCredit),
    reelDebit: cent(reelDebit), reelCredit: cent(reelCredit),
    // Écarts par sens, convention signée : positif = favorable.
    ecartDebit: cent(prevuDebit - reelDebit),
    ecartCredit: cent(reelCredit - prevuCredit),
  };
}

// Ce qu'une occurrence réclame ENCORE. Zéro dès qu'elle ne peut plus rien
// recevoir : soldée (le reste ne viendra pas), ignorée, absente, ou déjà
// dépassée. Toujours positif — un dépassement ne crée pas du reste négatif.
export function resteADepenser(occurrence) {
  const o = occurrence;
  if (!o || o.statut === 'soldee' || o.statut === 'ignoree' || o.statut === 'absente') return 0;
  return cent(Math.max(0, Math.abs(o.prevu || 0) - Math.abs(o.reel || 0)));
}

// ── Historique d'UNE prévision (fiche, 11/08) ────────────────────
// « Voir les échéances passées et celles rattachées à une opération du compte »
// (Cédric). On réutilise les statuts d'occurrence plutôt que d'inventer un
// second vocabulaire : ✅ réalisée · 🟡 partielle · ⚠️ manquante · 🔮 à venir.
//
// `passe` est PLAFONNÉ : sans borne, une ligne démarrée il y a des années
// produirait une liste sans fin. Le nombre tronqué est renvoyé pour que
// l'écran puisse le dire — cacher sans le dire serait pire que tronquer.
// `horizon` est le nombre de mois à projeter, réglable par l'utilisateur.
export function historiqueLigne({
  ligne, actuals = [], exceptions = [], rattachements = null,
  auj, passeMax = 12, horizon = 12,
}) {
  if (!ligne) return { passees: [], aVenir: [], tronquees: 0, fin: null, parAn: 0 };
  const courant = moisCourant(auj);
  const debut = moisDeDate(ligne.debut);

  const occ = (mk) => occurrencesDuMois({
    lignes: [ligne], actuals, exceptions, rattachements, mk, auj,
  })[0] || null;

  // Passé : du début (ou du plafond) jusqu'au mois courant INCLUS — le mois en
  // cours se lit avec son réel, il appartient au constat, pas à la projection.
  const toutesPassees = [];
  if (RE_MOIS.test(debut)) {
    for (const mk of moisEntre(debut, courant)) {
      const o = occ(mk);
      if (o) toutesPassees.push(o);
    }
  }
  const tronquees = Math.max(0, toutesPassees.length - passeMax);
  const passees = toutesPassees.slice(-passeMax);

  // À venir : les `horizon` mois qui suivent, sans les mois vides.
  const aVenir = [];
  let mk = decalerMois(courant, 1);
  for (let i = 0; i < horizon; i++) {
    const o = occ(mk);
    if (o) aVenir.push(o);
    mk = decalerMois(mk, 1);
  }

  // Dernière échéance quand la ligne a un terme : la date seule serait moins
  // parlante que la date AVEC son montant (l'inflation le fait bouger).
  let fin = null;
  if (ligne.fin) {
    const mkFin = moisDeDate(ligne.fin);
    for (const m of [...moisEntre(debut, mkFin)].reverse()) {
      if (estDue(ligne, m)) { fin = occ(m) || { mois: m, prevu: montantPrevu(ligne, m) }; break; }
    }
  }
  // Sans terme, ce qui informe n'est pas « ∞ » mais ce que la ligne ENGAGE :
  // le champ « Fin : sans fin » le dit déjà, le montant annuel non.
  const pas = { mensuelle: 1, trimestrielle: 3, semestrielle: 6, annuelle: 12 }[ligne.frequence] || 1;
  const parAn = ligne.frequence === 'ponctuelle' || ligne.frequence === 'personnalisee'
    ? 0 : cent((Math.abs(Number(ligne.montant) || 0) * 12) / pas);

  return { passees, aVenir, tronquees, fin, parAn };
}

// ── Moyenne du RÉEL sur les échéances passées (Cédric, 14/08) ────
// Le prévu est une intention ronde (4 000 €) ; le réel, lui, varie à chaque
// mois. Sans cette moyenne, l'écart se lit ligne à ligne et ne se totalise
// jamais — alors que c'est lui qui dit si la prévision est bien calibrée.
//
// ⚠️ Ne comptent QUE les mois où quelque chose a été constaté. Inclure les
// mois à zéro — non constatés, à venir, ignorés — ferait chuter la moyenne
// pour une raison qui n'a rien à voir avec les montants reçus.
// `nb` est renvoyé parce qu'une moyenne sans son effectif ne veut rien dire :
// sur un seul mois, ce n'est pas une moyenne, c'est un relevé.
export function moyenneReelle(occurrences = []) {
  const avecReel = occurrences.filter(o => o && Math.abs(o.reel || 0) > 0.005);
  if (!avecReel.length) return { montant: 0, nb: 0, ecart: 0 };
  const somme = avecReel.reduce((n, o) => n + Math.abs(o.reel), 0);
  const montant = cent(somme / avecReel.length);
  const prevuMoyen = cent(avecReel.reduce((n, o) => n + Math.abs(o.prevu || 0), 0) / avecReel.length);
  return { montant, nb: avecReel.length, ecart: cent(montant - prevuMoyen) };
}

// Moyenne RELEVÉE d'une ligne (Cédric, 16/08) : la moyenne des réels
// constatés — depuis le dernier ANNIVERSAIRE d'inflation quand la ligne en
// porte une (mélanger l'avant et l'après fausserait la moyenne), sinon sur
// les 12 derniers mois. C'est le chiffre qui dit si le prévu tient la route.
export function moyenneRelevee(ligne, { actuals = [], exceptions = [], rattachements = null } = {}, auj) {
  if (!ligne) return { montant: 0, nb: 0 };
  const courant = moisCourant(auj);
  let fenetre = 12;
  if (Number(ligne.inflationTaux) || 0) {
    const depuis = moisDeDate(ligne.inflationDepuis || ligne.debut);
    if (RE_MOIS.test(depuis)) {
      const [ay, am] = depuis.split('-').map(Number);
      const [by, bm] = courant.split('-').map(Number);
      const ecart = (by - ay) * 12 + (bm - am);
      // Mois écoulés depuis le dernier anniversaire, mois courant inclus.
      fenetre = Math.max(1, (((ecart % 12) + 12) % 12) + 1);
    }
  }
  const { passees } = historiqueLigne({
    ligne, actuals, exceptions, rattachements, auj, passeMax: fenetre, horizon: 0,
  });
  const { montant, nb } = moyenneReelle(passees);
  return { montant, nb };
}

// Projection MENSUELLE (Cédric, 16/08) : le prévu signé de chaque mois entre
// `depuis` (par défaut le mois courant) et `horizon` mois plus loin — sans
// accès réseau, `actuals: []` comme `prevuDuMoisParBudget` : un mois futur
// n'a pas de réel, seulement un prévu. C'est le brique qui manquait à
// `soldeFinDeMois` (qui ne projette PAS, par construction) pour bâtir un
// solde cumulé au-delà du mois courant.
export function projectionMensuelle({ lignes = [], exceptions = [], depuis, horizon = 12, auj } = {}) {
  const debut = depuis || moisCourant(auj);
  const mois = [];
  for (let i = 0; i < Math.max(0, horizon); i++) {
    const mk = decalerMois(debut, i);
    const occ = occurrencesDuMois({ lignes, actuals: [], exceptions, mk, auj });
    mois.push({ mois: mk, ...totauxMois(occ, []) });
  }
  return mois;
}

// ── Saut au mois voisin qui porte des échéances (Cédric, 15/08) ──
// Le pendant de `moisVoisinAvecOperations` du relevé : les doubles flèches
// « » sautent les mois vides. Ici « vide » = aucune ligne due ce mois.
// Borné : au-delà de `portee` mois sans rien, on rend null et la flèche se
// grise — chercher indéfiniment dans un budget vide bouclerait pour rien.
export function moisVoisinAvecPrevu(lignes = [], mois, dir, portee = 36) {
  let mk = mois;
  for (let i = 0; i < portee; i++) {
    mk = decalerMois(mk, dir);
    if (lignes.some(l => l && estDue(l, mk))) return mk;
  }
  return null;
}
